import {
  Div,
  Img,
  Tab,
  TextWithTooltipIcon,
  TooltipWrapper,
  renderProviderImage,
} from '@vgrubs/components';
// `Provider` is read at module top-level (in DIMENSION_PRESETS below), so it
// must come from the direct source, NOT the barrel — this file is pulled into
// the barrel's own init graph, and a top-level barrel read runs before the
// barrel namespace is populated ("Cannot read properties of undefined").
import { Provider } from '../../../types/shared.types';
import {
  AspectRatioValue,
  DEFAULT_ASPECT_RATIO,
} from './AspectRatioPicker';

type DimensionPreset = {
  provider: Provider;
  label: string;
  /** AspectRatioPicker chip this preset activates (must exist in its RATIOS). */
  selection: string;
  width: number;
  height: number;
  /** Requirement lines shown in the hover card. */
  details: string[];
};

/**
 * Image requirements per delivery platform (researched July 2026 — official
 * merchant docs where published: help.uber.com, DoorDash merchant learning
 * center, learn.grubhub.com, support.chownow.com, Oracle Simphony↔GloriaFood
 * integration docs). Only providers with documented restrictions are listed —
 * the rest of the `Provider` enum (delivery.com, EatStreet, Menufy, Local For
 * You, Nabe, POS/AI providers) publish none. Preset px are comfortably above
 * each platform's minimum; `selection` is the picker chip the ratio maps to.
 */
const DIMENSION_PRESETS: DimensionPreset[] = [
  {
    provider: Provider.ubereats,
    label: 'Uber Eats',
    selection: '5:4',
    width: 1200,
    height: 960,
    details: [
      'Aspect ratio: 5:4 to 3:2',
      'Width 550–10,000 px · height 440–10,000 px',
      'JPG, PNG or GIF · up to 10 MB',
    ],
  },
  {
    provider: Provider.doordash,
    label: 'DoorDash',
    selection: '16:9',
    width: 1600,
    height: 900,
    details: [
      'Aspect ratio: 16:9 landscape',
      'Minimum 1400 × 800 px',
      'JPG or PNG · up to 2 MB',
    ],
  },
  {
    provider: Provider.grubhub,
    label: 'Grubhub',
    selection: '1:1',
    width: 1400,
    height: 1400,
    details: [
      'Aspect ratio: 1:1 (square)',
      'Recommended 1400 × 1400 px',
      'JPG or PNG',
    ],
  },
  {
    provider: Provider.chownow,
    label: 'ChowNow',
    selection: '1:1',
    width: 1200,
    height: 1200,
    details: ['Aspect ratio: 1:1 (square)', 'Minimum 1200 × 1200 px'],
  },
  {
    provider: Provider.gloriafood,
    label: 'GloriaFood',
    selection: '3:2',
    width: 1200,
    height: 800,
    details: [
      'Aspect ratio: about 3:2',
      'Minimum 335 × 225 px',
      'JPEG · up to 1 MB',
    ],
  },
];

const TITLE_TOOLTIP =
  'Some delivery platforms (Uber Eats, DoorDash, Grubhub…) only accept item ' +
  'photos that meet their size rules. Click a platform to preset the matching ' +
  'aspect ratio, width and height below — hover one to see its exact ' +
  'requirements. You can still adjust the values afterwards.';

// Multi-line hover card via the global tooltip's `data-tooltip-html`.
const presetTooltip = (p: DimensionPreset) =>
  [
    `<b>${p.label}</b>`,
    ...p.details,
    `Sets ${p.selection} · ${p.width} × ${p.height} px`,
  ].join('<br />');

/**
 * "Dimension presets" — one logo tab per delivery platform with documented
 * image restrictions (the partner-onboarding provider-tab idiom). Hover shows
 * the platform's requirements; click writes `{ selection, width, height }`
 * into the AspectRatioPicker value below (still freely editable after, which
 * also deselects the tab since the active state is derived by comparison).
 * Clicking the active tab toggles back to Auto.
 */
export const DimensionPresets = ({
  value,
  onSelect,
  disabled,
}: {
  value: AspectRatioValue;
  onSelect: (value: AspectRatioValue) => void;
  disabled?: boolean;
}) => {
  const isActive = (p: DimensionPreset) =>
    value.selection === p.selection &&
    value.width === p.width &&
    value.height === p.height;

  return (
    <div>
      <Div $mb="8">
        <TextWithTooltipIcon text="Dimension presets" tooltip={TITLE_TOOLTIP} />
      </Div>
      <Div
        $flex
        $gap="8px"
        $wrap
        style={disabled ? { opacity: 0.6, pointerEvents: 'none' } : undefined}
      >
        {DIMENSION_PRESETS.map((p) => {
          const active = isActive(p);
          return (
            <TooltipWrapper key={p.provider} tooltip={presetTooltip(p)}>
              <Tab
                $minWidthAuto
                $minWidthAutoSM
                selected={active}
                onClick={() =>
                  onSelect(
                    active
                      ? DEFAULT_ASPECT_RATIO
                      : {
                          selection: p.selection,
                          width: p.width,
                          height: p.height,
                        }
                  )
                }
              >
                <Img src={renderProviderImage(p.provider)} $height="22px" />
              </Tab>
            </TooltipWrapper>
          );
        })}
      </Div>
    </div>
  );
};
